import { DateValue } from '@givebutter/primitives/date-picker';
import {
    QUICKBOOKS_SYNC_STATUS_MAP,
    QuickBooksTransactionSyncStatus,
} from 'assets/js/pages/account-settings/account-integrations/QuickBooks/constants';
import { FilterOperator, FilterRequestOperator, PaymentMethod } from 'components/filter/types';
import { formatDate, transformDateRangeTypeToReadable } from 'helpers/dates';
import { ActivityType } from 'types/domain';

interface MultiSelectReadableOptions<T> {
    transform?: (value: T) => string;
    lengthOnly?: boolean;
    cutoffLength?: number;
}

const RESOURCE_CUTOFF_LENGTH = 3;
const RESOURCE_BASE_STR = 'Selected';
const transformMultiselectToReadable = <T>(
    values: T[],
    options: MultiSelectReadableOptions<T> = {},
) => {
    const { transform, lengthOnly = false, cutoffLength = RESOURCE_CUTOFF_LENGTH } = options;
    const length = values.length;
    if (length === 0) {
        return `0 ${RESOURCE_BASE_STR}`;
    }

    if (length > cutoffLength || lengthOnly) {
        return `${values.length} ${RESOURCE_BASE_STR}`;
    }

    return values.map(value => (transform ? transform(value) : value)).join(', ');
};

function transformOperatorToReadable(operator: string): string;
function transformOperatorToReadable(operator: FilterOperator): string;
function transformOperatorToReadable(operator: any): string {
    switch (operator as FilterOperator) {
        case 'lt':
            return 'less than';
        case 'lte':
            return 'less than or equal to';
        case 'eq':
            return 'equals';
        case 'neq':
            return 'is not equal to';
        case 'gt':
            return 'greater than';
        case 'gte':
            return 'greater than or equal to';
        case 'bet':
            return 'between';
        case 'is_empty':
            return 'is empty';
        case 'is_not_empty':
            return 'is not empty';
        case 'all':
        case 'any':
        case 'none':
        default:
            return operator;
    }
}

function transformDateOperatorToReadable(operator: string): string;
function transformDateOperatorToReadable(operator: FilterOperator): string;
function transformDateOperatorToReadable(operator: any): string {
    switch (operator as FilterOperator) {
        case 'lt':
            return 'before';
        case 'lte':
            return 'on or before';
        case 'eq':
            return 'on';
        case 'neq':
            return 'is not on';
        case 'gt':
            return 'after';
        case 'gte':
            return 'on or after';
        case 'bet':
            return 'between';
        case 'is_empty':
            return 'is empty';
        case 'is_not_empty':
            return 'is not empty';
        case 'TODAY':
        case 'YESTERDAY':
        case 'LAST-WEEK':
        case 'LAST-TWO-WEEKS':
        case 'LAST-MONTH':
        case 'LAST-QUARTER':
        case 'LAST-YEAR':
        case 'MTD':
        case 'QTD':
        case 'YTD':
        case 'ALL-TIME':
        case 'CUSTOM':
            return transformDateRangeTypeToReadable(operator);
        case 'includes':
        case 'excludes':
        case 'all':
        case 'any':
        case 'none':
        default:
            return operator;
    }
}

const transformToFormOperator = (operator: FilterRequestOperator): FilterOperator => {
    switch (operator) {
        case '<':
            return 'lt';
        case '<=':
            return 'lte';
        case '>':
            return 'gt';
        case '>=':
            return 'gte';
        case '=':
            return 'eq';
        case '<>':
            return 'neq';
        case 'between':
            return 'bet';
        case 'is_empty':
        case 'is_not_empty':
        case 'includes':
        case 'excludes':
        case 'all':
        case 'any':
        case 'none':
        case 'TODAY':
        case 'YESTERDAY':
        case 'LAST-WEEK':
        case 'LAST-TWO-WEEKS':
        case 'LAST-MONTH':
        case 'LAST-QUARTER':
        case 'LAST-YEAR':
        case 'MTD':
        case 'QTD':
        case 'YTD':
        case 'ALL-TIME':
        case 'CUSTOM':
            return operator;
    }
};

const transformDateToReadable = (date: DateValue) => {
    if (Array.isArray(date)) {
        if (date.length < 2) {
            return '';
        }
        const [startDate, endDate] = date;
        return `${startDate ? formatDate(startDate) : ''} and ${
            endDate ? formatDate(endDate) : ''
        }`;
    }

    return formatDate(date);
};

const transformPaymentMethodToReadable = (method: PaymentMethod) => {
    switch (method) {
        case 'digital_wallet':
            return 'Digital Wallet';
        case 'donor_advised_fund':
            return 'Donor Advised Fund';
        default:
            return method;
    }
};

const transformContactActivityToReadable = (activity: ActivityType) => {
    switch (activity) {
        case 'email':
            return 'Email';
        case 'phone_call':
            return 'Phone call';
        case 'sms':
            return 'SMS';
        case 'note':
            return 'Note';
        case 'email.subscribed':
            return 'Email subscribed';
        case 'email.unsubscribed':
            return 'Email unsubscribed';
        case 'sms.subscribed':
            return 'SMS subscribed';
        case 'sms.unsubscribed':
            return 'SMS unsubscribed';
        case 'transaction.succeeded':
            return 'Transaction';
        case 'recurring_plan.created':
            return 'Recurring plan';
        case 'campaign.joined':
            return 'Joined campaign';
        case 'ticket.issued':
            return 'Ticket';
        case 'signup_form.submitted':
            return 'Signup form';
        default:
            return activity;
    }
};

const transformQuickBooksTransactionStatusToReadable = (status: QuickBooksTransactionSyncStatus) =>
    QUICKBOOKS_SYNC_STATUS_MAP[status] ?? status;

export {
    transformContactActivityToReadable,
    transformMultiselectToReadable,
    transformOperatorToReadable,
    transformToFormOperator,
    transformDateOperatorToReadable,
    transformDateToReadable,
    transformPaymentMethodToReadable,
    transformQuickBooksTransactionStatusToReadable,
};
